import { useEffect, useState } from "react";
import { WifiOff, RefreshCw, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { subscribe, syncQueue, removeEntry } from "@/lib/offlineQueue";

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
}

export function OfflineBanner() {
  const online = useOnline();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => subscribe(setQueue), []);

  const sync = async () => {
    setBusy(true);
    const r = await syncQueue();
    setBusy(false);
    if (r.synced) toast.success(`${r.synced} offline action${r.synced > 1 ? "s" : ""} synced`);
    if (r.failed) toast.error(`${r.failed} queued action${r.failed > 1 ? "s" : ""} rejected by server — review below`);
    window.dispatchEvent(new Event("wipplops:synced"));
  };

  useEffect(() => {
    if (online && queue.length && !queue.every((e) => e.error)) sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  if (online && queue.length === 0) return null;
  const failed = queue.filter((e) => e.error);
  return (
    <div className={`border-b px-4 py-2 text-xs flex flex-wrap items-center gap-3 ${online ? "bg-signal-amber/10 border-signal-amber/40 text-signal-amber" : "bg-signal-red/10 border-signal-red/40 text-signal-red"}`} data-testid="offline-banner">
      {online ? <CloudUpload className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
      <span className="font-mono uppercase tracking-widest">
        {online ? `${queue.length} floor action${queue.length > 1 ? "s" : ""} pending sync` : `Offline — ${queue.length} action${queue.length === 1 ? "" : "s"} saved locally, will sync when back online`}
      </span>
      {online && (
        <button onClick={sync} disabled={busy} className="ml-auto inline-flex items-center gap-1 underline" data-testid="offline-sync-now">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Sync now
        </button>)}
      {failed.length > 0 && (
        <ul className="w-full space-y-1" data-testid="offline-failed-list">
          {failed.map((e) => (
            <li key={e.id} className="flex items-center gap-2 text-foreground/80">
              <span className="metric">{e.taskId}</span> · {e.action} — <span className="text-signal-red">{e.error}</span>
              <button className="underline ml-auto" onClick={() => removeEntry(e.id)} data-testid={`offline-discard-${e.id}`}>Discard</button>
            </li>))}
        </ul>)}
    </div>
  );
}
